<template>
  <div>
    <PTransparentLoader :loading="loading">
      <CRow v-if="certification">
        <CCol lg="12">
          <div class="d-flex align-items-center mb-4">
            <CButton
              color="link"
              class="p-0 mr-3"
              @click="$router.push({name: 'adminCertificationList'})"
            >
              <CIcon name="cil-arrow-left" size="lg"/>
            </CButton>
            <h1 class="main-header mb-0">
              <translate translate-context="admin-certification">Certification</translate>
              #{{ certification.id }}
            </h1>
            <CBadge :color="getStatusColor(certification.status)" class="ml-3">
              {{ getStatusLabel(certification.status) }}
            </CBadge>
          </div>

          <!-- Contract Manager Info -->
          <CCard class="mb-3">
            <CCardHeader>
              <translate translate-context="admin-certification">Contract Manager</translate>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="4">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Name</translate>
                    </small>
                    <strong>{{ certification.contract_manager_name }}</strong>
                  </div>
                </CCol>
                <CCol md="4">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Email</translate>
                    </small>
                    {{ certification.contract_manager_email || '-' }}
                  </div>
                </CCol>
                <CCol md="4">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Region</translate>
                    </small>
                    <CBadge :color="certification.region === 'eu' ? 'info' : 'secondary'">
                      {{ certification.region === 'eu' ? 'EU' : 'Extra EU' }}
                    </CBadge>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Certification Details -->
          <CCard class="mb-3">
            <CCardHeader>
              <translate translate-context="admin-certification">Certification Details</translate>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">VAT Regime</translate>
                    </small>
                    <CBadge :color="certification.vat_regime ? 'success' : 'secondary'">
                      {{ certification.vat_regime ? 'Yes' : 'No' }}
                    </CBadge>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">VAT Number</translate>
                    </small>
                    <code v-if="certification.vat_number">{{ certification.vat_number }}</code>
                    <span v-else class="text-muted">-</span>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">IBAN</small>
                    <code v-if="certification.iban">{{ certification.iban }}</code>
                    <span v-else class="text-muted">-</span>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Document</translate>
                    </small>
                    <a
                      v-if="certification.document"
                      :href="certification.document"
                      target="_blank"
                      class="btn btn-sm btn-outline-primary"
                    >
                      <CIcon name="cil-cloud-download" class="mr-1"/>
                      <translate translate-context="admin-certification">Download PDF</translate>
                    </a>
                    <span v-else class="text-muted">-</span>
                  </div>
                </CCol>
              </CRow>

              <CRow>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Submitted At</translate>
                    </small>
                    {{ formatDate(certification.submitted_at) }}
                  </div>
                </CCol>
                <CCol md="3" v-if="certification.approved_at">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Approved At</translate>
                    </small>
                    {{ formatDate(certification.approved_at) }}
                  </div>
                </CCol>
              </CRow>

              <CRow v-if="certification.rejected_reason">
                <CCol>
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-certification">Rejected Reason</translate>
                    </small>
                    <CAlert color="danger" class="mb-0 py-2">
                      {{ certification.rejected_reason }}
                    </CAlert>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Actions -->
          <CCard class="mb-3" v-if="(canApprove || canReject) && certification.status !== CERTIFICATION_STATUS.REJECTED">
            <CCardBody>
              <div class="d-flex">
                <CButton
                  v-if="canApprove && certification.status === CERTIFICATION_STATUS.PENDING"
                  color="success"
                  @click="handleApprove"
                  class="mr-2"
                >
                  <CIcon name="cil-check" class="mr-1"/>
                  <translate translate-context="admin-certification">Approve</translate>
                </CButton>
                <CButton
                  v-if="canReject"
                  color="danger"
                  variant="outline"
                  @click="showRejectModal = true"
                >
                  <CIcon name="cil-x" class="mr-1"/>
                  <translate translate-context="admin-certification">Reject</translate>
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PTransparentLoader>

    <!-- Reject Modal -->
    <CModal
      :show.sync="showRejectModal"
      size="sm"
    >
      <template #header>
        <h5 class="modal-title">
          <translate translate-context="admin-certification">Reject Certification</translate>
        </h5>
        <CButtonClose @click="showRejectModal = false" />
      </template>
      <template #default>
        <div class="form-group">
          <label class="font-weight-bold">
            <translate translate-context="admin-certification">Reason</translate>
          </label>
          <CTextarea
            v-model="rejectReason"
            :placeholder="$pgettext('admin-certification', 'Enter the reason for rejection...')"
            rows="3"
          />
        </div>
      </template>
      <template #footer>
        <CButton color="secondary" variant="outline" @click="showRejectModal = false">
          <translate translate-context="admin-certification">Cancel</translate>
        </CButton>
        <CButton
          color="danger"
          :disabled="!rejectReason.trim()"
          @click="handleReject"
        >
          <translate translate-context="admin-certification">Reject</translate>
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import {
    CERTIFICATION_STATUS,
    CERTIFICATION_STATUS_LABELS,
    CERTIFICATION_STATUS_COLORS
} from './constants'

export default {
    name: 'AdminCertificationDetail',
    components: {
        PTransparentLoader
    },
    data() {
        return {
            loading: false,
            showRejectModal: false,
            rejectReason: '',
            CERTIFICATION_STATUS
        }
    },
    computed: {
        ...mapGetters('adminCertification', ['certification']),
        ...mapGetters('login', ['hasAnyPerms']),

        canApprove() {
            return this.hasAnyPerms(['admin.certifications.approve'])
        },
        canReject() {
            return this.hasAnyPerms(['admin.certifications.reject'])
        }
    },
    methods: {
        ...mapActions('adminCertification', ['fetchDetail', 'approve', 'reject']),
        ...mapActions('login', ['fetchConfig']),

        getStatusLabel(status) {
            return CERTIFICATION_STATUS_LABELS[status] || status
        },

        getStatusColor(status) {
            return CERTIFICATION_STATUS_COLORS[status] || 'light'
        },

        formatDate(dateString) {
            if (!dateString) return '-'
            return new Date(dateString).toLocaleString()
        },

        async handleApprove() {
            const confirmed = await this.confirm(
                this.$pgettext('admin-certification', 'Approve Certification'),
                this.$pgettext('admin-certification', 'Are you sure you want to approve this certification?')
            )
            if (!confirmed) return

            try {
                await this.approve(this.$route.params.id)
                this.notifySuccess(this.$pgettext('admin-certification', 'Certification approved successfully'))
            } catch (error) {
                this.notifyError(this.$pgettext('admin-certification', 'Error approving certification'))
            }
        },

        async handleReject() {
            if (!this.rejectReason.trim()) return

            try {
                await this.reject({
                    id: this.$route.params.id,
                    rejectedReason: this.rejectReason.trim()
                })
                this.showRejectModal = false
                this.notifySuccess(this.$pgettext('admin-certification', 'Certification rejected'))
            } catch (error) {
                this.notifyError(this.$pgettext('admin-certification', 'Error rejecting certification'))
            }
        }
    },
    async mounted() {
        await this.fetchConfig({ type: 'admin_certifications' })

        this.loading = true
        try {
            await this.fetchDetail(this.$route.params.id)
        } catch (error) {
            this.notifyError(this.$pgettext('admin-certification', 'Error loading certification'))
            this.$router.push({ name: 'adminCertificationList' })
        } finally {
            this.loading = false
        }
    }
}
</script>
